/**
 * Single source of truth for turning an axios error into a *string*
 * suitable for display.
 *
 * The backend's current error envelope is `{ error: { code, message, status } }`
 * (see serverless-portfolio-tracker `utils/handlerResponses.js#errorEnvelope`),
 * but several legacy endpoints still return `{ error: "string" }` or
 * `{ message: "string" }`. This helper handles all three shapes and always
 * returns a string.
 *
 * Without this guarantee, callers risk shoving an error *object* into React
 * state and triggering React error #31 ("Objects are not valid as a React
 * child") on the next render — which has bitten us in ApiKeysTab and would
 * have bitten userSettingsSlice / disclaimerSlice / tradeNotesSlice the
 * moment any of their endpoints started returning the new envelope shape.
 *
 * @param {unknown} error - The caught error (typically an axios error)
 * @param {string} [fallback='Unexpected error'] - Message to use if no
 *   message can be extracted from the error
 * @returns {string} A guaranteed-string error message
 */
export const extractErrorMessage = (error, fallback = 'Unexpected error') => {
  const data = error?.response?.data;

  // New envelope: { error: { code, message, status } }
  if (data?.error && typeof data.error === 'object' && typeof data.error.message === 'string') {
    return data.error.message;
  }

  // Legacy envelope: { error: "string" }
  if (typeof data?.error === 'string') {
    return data.error;
  }

  // Older envelope: { message: "string" }
  if (typeof data?.message === 'string') {
    return data.message;
  }

  // axios's own error.message (e.g. "Network Error")
  if (typeof error?.message === 'string') {
    return error.message;
  }

  return fallback;
};
